import { lazy, Suspense } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  ErrorComponent,
  Outlet,
  useMatches,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router'

import { AppHeader } from '@/components/app-header'
import { QueryErrorState } from '@/components/query-error-state'
import { leafRouteId, shellFor } from '@/config/navigation'
import { RequireSession } from '@/features/auth/components/require-session'
import {
  CurrentUserReadError,
  requirePageAccess,
} from '@/features/auth/model/page-access'

// Router devtools are loaded lazily and stripped from production builds.
const RouterDevtools = import.meta.env.PROD
  ? () => null
  : lazy(() =>
      import('@tanstack/react-router-devtools').then((module) => ({
        default: module.TanStackRouterDevtools,
      })),
    )

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // The wrapper breaks a type-inference cycle between the root route and registered router.
  beforeLoad: (context): ReturnType<typeof requirePageAccess> =>
    requirePageAccess(context),
  errorComponent: SessionLoadError,
  component: RootLayout,
})

function SessionLoadError(props: ErrorComponentProps) {
  const router = useRouter()
  if (!(props.error instanceof CurrentUserReadError)) return <ErrorComponent {...props} />

  return (
    <div className="flex h-full items-center justify-center bg-background p-6">
      <QueryErrorState
        message="Couldn’t reach the server."
        onRetry={() => void router.invalidate()}
      />
    </div>
  )
}

function RootLayout() {
  const routeId = useMatches({ select: leafRouteId })
  const shell = shellFor(routeId)
  if (shell === 'public') return <ChromelessLayout />

  if (shell === 'bare') {
    return (
      <RequireSession>
        <ChromelessLayout />
      </RequireSession>
    )
  }

  return (
    <RequireSession>
      <AppShellLayout />
    </RequireSession>
  )
}

function ChromelessLayout() {
  return (
    <div className="h-full bg-background text-foreground">
      <Outlet />
      <Devtools />
    </div>
  )
}

function AppShellLayout() {
  return (
    <div className="flex h-full flex-col bg-background text-foreground">
      <AppHeader />
      <main className="min-h-0 flex-1">
        <Outlet />
      </main>
      <Devtools />
    </div>
  )
}

function Devtools() {
  return (
    <Suspense fallback={null}>
      <RouterDevtools position="bottom-left" />
    </Suspense>
  )
}
